import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { createMigration, getTenancyRelationships, validateOdooCredentials, createOrUpdateMigrationConfig } from '../../api/migrations'
import {
  SOURCE_SYSTEMS,
  DESTINATION_SYSTEMS,
  getDefaultMigrationName,
  deriveDestinationSystem,
  formatOdooVersion,
  cleanOdooUrl,
  ProgressHeader,
  StepMigrationDetails,
  StepOrganization,
  StepOdooConnection
} from './MigrationForm/index'

// Re-export constants for backward compatibility
export { SOURCE_SYSTEMS, DESTINATION_SYSTEMS, formatOdooVersion }

const MigrationForm = ({ onSuccess, onCancel }) => {
  const { user } = useAuth()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [availableOrganizations, setAvailableOrganizations] = useState([])
  const [loadingOrganizations, setLoadingOrganizations] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [createNewClient, setCreateNewClient] = useState(false)

  const activeTenancy = user?.tenancies?.find(t => t.id === user.active_tenancy_id)
  const isServiceProvider = activeTenancy?.tenancy_type === 'SERVICE_PROVIDER'

  const [formData, setFormData] = useState({
    name: getDefaultMigrationName(),
    description: '',
    source_system: '',
    tenancy_id: '',
    new_client_name: '',
    new_client_email: '',
    new_client_industry: '',
    new_client_currency: 'EUR'
  })

  // Odoo connection state
  const [odooUrl, setOdooUrl] = useState('')
  const [odooUser, setOdooUser] = useState('')
  const [odooPassword, setOdooPassword] = useState('')
  const [odooDb, setOdooDb] = useState('')
  const [showDatabaseField, setShowDatabaseField] = useState(false)
  const [availableDatabases, setAvailableDatabases] = useState([])

  // Verification state
  const [verifying, setVerifying] = useState(false)
  const [verificationResult, setVerificationResult] = useState(null)
  const [companies, setCompanies] = useState([])
  const [selectedCompanyId, setSelectedCompanyId] = useState(null)
  const [odooVersion, setOdooVersion] = useState(null)

  const totalSteps = 3

  useEffect(() => {
    loadAvailableOrganizations()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.active_tenancy_id])

  const loadAvailableOrganizations = async () => {
    try {
      setLoadingOrganizations(true)

      const organizations = [{
        id: activeTenancy.id,
        name: activeTenancy.name,
        tenancy_type: activeTenancy.tenancy_type,
        isSelf: true
      }]

      if (isServiceProvider) {
        try {
          const relationships = await getTenancyRelationships(user.active_tenancy_id)
          if (relationships.managed_clients) {
            relationships.managed_clients.forEach(rel => {
              if (rel.status === 'ACTIVE' && rel.client) {
                organizations.push({
                  id: rel.client.id,
                  name: rel.client.name,
                  tenancy_type: rel.client.tenancy_type,
                  isSelf: false
                })
              }
            })
          }
        } catch (err) {
          console.error('Failed to load relationships:', err)
        }
      }

      setAvailableOrganizations(organizations)
    } catch (err) {
      console.error('Failed to load organizations:', err)
    } finally {
      setLoadingOrganizations(false)
    }
  }

  const filteredOrganizations = availableOrganizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const selfOrg = availableOrganizations.find(c => c.isSelf)
  const clientOrgs = filteredOrganizations.filter(c => !c.isSelf)

  const handleVerifyConnection = async () => {
    setVerifying(true)
    setVerificationResult(null)
    setError(null)
    setAvailableDatabases([])

    // Clean the URL before verification
    const cleanedUrl = cleanOdooUrl(odooUrl)
    if (cleanedUrl !== odooUrl) {
      setOdooUrl(cleanedUrl)
    }

    try {
      const result = await validateOdooCredentials({
        odoo_url: cleanedUrl,
        database: odooDb || null,
        username: odooUser,
        password: odooPassword
      })

      setVerificationResult(result)

      if (result.valid) {
        setCompanies(result.companies || [])
        setOdooVersion(result.version)
        if (result.database) {
          setOdooDb(result.database)
        }
        if (result.companies?.length === 1) {
          setSelectedCompanyId(result.companies[0].id)
        }
      } else if (result.error_code === 'MULTIPLE_DATABASES' && result.databases) {
        setAvailableDatabases(result.databases)
        setShowDatabaseField(true)
      } else if (result.error_code === 'DATABASE_REQUIRED') {
        setShowDatabaseField(true)
      }
    } catch (err) {
      setError(err.message)
      setVerificationResult({ valid: false, error_message: err.message })
    } finally {
      setVerifying(false)
    }
  }

  const handleOdooFieldChange = (field, value) => {
    setVerificationResult(null)
    setCompanies([])
    setSelectedCompanyId(null)
    setOdooVersion(null)
    setAvailableDatabases([])

    switch (field) {
      case 'url':
        setOdooUrl(value)
        setShowDatabaseField(false)
        setOdooDb('')
        break
      case 'user':
        setOdooUser(value)
        break
      case 'password':
        setOdooPassword(value)
        break
      case 'db':
        setOdooDb(value)
        break
    }
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    try {
      setLoading(true)
      setError(null)

      if (!formData.tenancy_id) {
        setError('Please select an organization for this migration')
        setLoading(false)
        return
      }

      const destination_system = deriveDestinationSystem(odooVersion)

      const migrationData = {
        name: formData.name,
        description: formData.description,
        source_system: formData.source_system,
        destination_system: destination_system,
        tenancy_id: formData.tenancy_id
      }

      const migration = await createMigration(migrationData)

      const selectedCompany = companies.find(c => c.id === selectedCompanyId)
      await createOrUpdateMigrationConfig(migration.id, {
        odoo_url: odooUrl,
        odoo_version: odooVersion,
        db_name: odooDb,
        db_user: odooUser,
        db_password: odooPassword,
        company_id: selectedCompanyId,
        company_name: selectedCompany?.name || null,
        rewrite_doc_number: true
      })

      onSuccess(migration)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleFormDataChange = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }

  const canProceedToStep2 = () => formData.source_system && formData.name
  const canProceedToStep3 = () => formData.tenancy_id || (createNewClient && formData.new_client_name)
  const canCreateMigration = () => verificationResult?.valid && selectedCompanyId

  const selectedOrganization = availableOrganizations.find(c => c.id === formData.tenancy_id)

  const getStepName = (step) => {
    if (step === 1) return 'Migration Details'
    if (step === 2) return 'Organization'
    return 'Odoo Connection'
  }

  const getNextStepHint = (step) => {
    if (step === 1) return 'Next: Select Organization'
    if (step === 2) return 'Next: Connect to Odoo'
    return 'Create migration'
  }

  return (
    <div className="space-y-6">
      <ProgressHeader
        currentStep={currentStep}
        totalSteps={totalSteps}
        stepName={getStepName(currentStep)}
        nextStepHint={getNextStepHint(currentStep)}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {currentStep === 1 && (
        <StepMigrationDetails
          formData={formData}
          onChange={handleFormDataChange}
        />
      )}

      {currentStep === 2 && (
        <StepOrganization
          formData={formData}
          onChange={handleFormDataChange}
          onGoBack={() => setCurrentStep(1)}
          isServiceProvider={isServiceProvider}
          selfOrg={selfOrg}
          clientOrgs={clientOrgs}
          loadingOrganizations={loadingOrganizations}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          createNewClient={createNewClient}
          onCreateNewClientChange={setCreateNewClient}
        />
      )}

      {currentStep === 3 && (
        <StepOdooConnection
          formData={formData}
          selectedOrganization={selectedOrganization}
          onGoToStep={setCurrentStep}
          odooUrl={odooUrl}
          odooUser={odooUser}
          odooPassword={odooPassword}
          odooDb={odooDb}
          showDatabaseField={showDatabaseField}
          availableDatabases={availableDatabases}
          onOdooFieldChange={handleOdooFieldChange}
          verifying={verifying}
          verificationResult={verificationResult}
          companies={companies}
          selectedCompanyId={selectedCompanyId}
          odooVersion={odooVersion}
          onVerifyConnection={handleVerifyConnection}
          onCompanySelect={setSelectedCompanyId}
        />
      )}

      {/* Footer Actions */}
      <div className="border-t border-gray-200 pt-6 flex justify-between items-center">
        <button
          type="button"
          onClick={currentStep === 1 ? onCancel : () => setCurrentStep(currentStep - 1)}
          disabled={loading}
          className="px-6 h-10 border border-gray-200 rounded-lg text-[#111418] font-bold text-sm hover:bg-gray-50 transition-all disabled:opacity-50"
        >
          {currentStep === 1 ? 'Cancel' : 'Back'}
        </button>

        <div className="flex items-center gap-3">
          {currentStep === 1 && (
            <button
              type="button"
              onClick={() => setCurrentStep(2)}
              disabled={!canProceedToStep2()}
              className="flex items-center gap-2 px-8 h-10 bg-primary text-white font-bold text-sm rounded-lg shadow-md shadow-primary/20 hover:bg-primary/90 transition-all disabled:opacity-50"
            >
              Next: Organization
              <span className="material-symbols-outlined text-sm">chevron_right</span>
            </button>
          )}

          {currentStep === 2 && (
            <button
              type="button"
              onClick={() => setCurrentStep(3)}
              disabled={!canProceedToStep3()}
              className="flex items-center gap-2 px-8 h-10 bg-primary text-white font-bold text-sm rounded-lg shadow-md shadow-primary/20 hover:bg-primary/90 transition-all disabled:opacity-50"
            >
              Next: Odoo Connection
              <span className="material-symbols-outlined text-sm">chevron_right</span>
            </button>
          )}

          {currentStep === 3 && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || !canCreateMigration()}
              className="flex items-center gap-2 px-8 h-10 bg-primary text-white font-bold text-sm rounded-lg shadow-md shadow-primary/20 hover:bg-primary/90 transition-all disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Migration'}
              {!loading && <span className="material-symbols-outlined text-sm">chevron_right</span>}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default MigrationForm
